import { reactive, ref, computed } from 'vue';

export type FormControl<T = any> = ReturnType<typeof createFormControl<T>>;

export type ValidatorFn<T = any> = {
  (value: T): string | null;
  __type?: string;
};

export function createFormControl<T = any>(initialValue: T, validators: ValidatorFn<T>[] = []) {
  const value = ref<T>(initialValue);
  const touched = ref(false);
  const focused = ref(false);
  const validatorsRef = ref<ValidatorFn<T>[]>([...validators]);

  const errors = computed(() =>
    validatorsRef.value
      .map(v => v(value.value))
      .filter((e): e is string => e !== null)
  );

  const isValid = computed(() => errors.value.length === 0);
  const isRequired = computed(() => validatorsRef.value.some(v => v.__type === 'required'));

  function setValue(v: T) {
    value.value = v;
  }

  function reset() {
    value.value = initialValue;
    touched.value = false;
    focused.value = false;
  }

  return reactive({
    value,
    touched,
    focused,
    validators: validatorsRef,
    errors,
    isValid,
    isRequired,
    setValue,
    reset,
    addValidator: (validator: ValidatorFn<T>) => {
      const type = (validator as any).__type;

      // Se o validator tiver type, garante unicidade por tipo
      if (type) {
        validatorsRef.value = validatorsRef.value.filter(
          v => (v as any).__type !== type
        );
        validatorsRef.value.push(validator);
        return;
      }

      // Fallback para validators sem type
      if (!validatorsRef.value.includes(validator)) {
        validatorsRef.value.push(validator);
      }
    },
    removeValidator: (validator: ValidatorFn<T>) => {
      const type = (validator as any).__type;

      if (type) {
        validatorsRef.value = validatorsRef.value.filter(
          v => (v as any).__type !== type
        );
      } else {
        validatorsRef.value = validatorsRef.value.filter(v => v !== validator);
      }
    },
    clearValidators: () => validatorsRef.value = [],
    markAsTouched: () => touched.value = true,
    markAsFocused: () => focused.value = true,
    markAsUnfocused: () => { focused.value = false; touched.value = true; }
  });
}


// tipagem de cada controle baseado no valor inicial
type ControlsConfig = Record<string, [any, ValidatorFn<any>[]?]>;

// tipagem de controles fortemente tipada
type ControlsFromConfig<T extends ControlsConfig> = {
  [K in keyof T]: FormControl<T[K][0]>;
};

export type FormGroup<T extends ControlsConfig = any> = ReturnType<typeof createFormGroup<T>>;

export function createFormGroup<T extends ControlsConfig>(
  controlsConfig: T
) {
  const controls: Partial<ControlsFromConfig<T>> = reactive({});

  for (const key in controlsConfig) {
    const [initialValue, validators = []] = controlsConfig[key];
    controls[key as keyof T] = createFormControl(initialValue, validators) as any;
  }

  const value = computed(() => {
    const values: Record<string, any> = {};
    for (const key in controls) {
      values[key] = controls[key]!.value;
    }
    return values as { [K in keyof T]: T[K][0] };
  });

  const isValid = computed(() =>
    Object.values(controls).every(control => control!.isValid)
  );

  const errors = computed(() => {
    const allErrors: Record<string, string[]> = {};
    for (const key in controls) {
      if (controls[key]!.errors.length > 0) {
        allErrors[key] = controls[key]!.errors;
      }
    }
    return allErrors;
  });

  function setValue(values: Partial<{ [K in keyof T]: T[K][0] }>) {
    for (const key in values) {
      if (controls[key]) {
        controls[key]!.setValue(values[key]!);
      }
    }
  }

  function reset() {
    for (const key in controls) {
      controls[key]!.reset();
    }
  }

  function markAllAsTouched() {
    for (const key in controls) {
      controls[key]!.markAsTouched();
    }
  }

  function addControl(name: keyof T, initialValue: T[typeof name][0], validators: ValidatorFn<any>[] = []) {
    controls[name] = createFormControl(initialValue, validators) as any;
  }

  function removeControl(name: keyof T) {
    delete controls[name];
  }

  function removeControls() {
    for (const key in controls) {
      delete controls[key];
    }
  }

  return reactive({
    controls: controls as ControlsFromConfig<T>,
    value,
    isValid,
    errors,
    setValue,
    reset,
    markAllAsTouched,
    addControl,
    removeControl,
    removeControls
  })
}
